// ---------------------------------------------------------------------------
// ETA FORMATTING
// ---------------------------------------------------------------------------
// Shared by the Head Office deliveries grid and the Customers screen so both
// read the same. The API returns minutes, miles and an ISO instant; every
// decision about how that LOOKS lives here and nowhere else.
//
// Everything is prefixed "about". These are estimates built from straight-line
// distance and an average speed, and the person reading the screen is usually
// about to say the number out loud to a customer on the phone. Wording that
// sounds like a promise is the one thing this must not do.

/** 8 -> "8 min", 95 -> "1 hr 35 min". */
export function fmtEtaMinutes(minutes) {
  if (minutes === null || minutes === undefined) return null;
  const m = Math.max(0, Math.round(Number(minutes)));
  if (!Number.isFinite(m)) return null;
  if (m < 1) return "under a minute";
  if (m < 60) return `${m} min`;
  const hrs = Math.floor(m / 60);
  const rem = m % 60;
  return rem ? `${hrs} hr ${rem} min` : `${hrs} hr`;
}

/** ISO instant -> "14:35" in the reader's own timezone. */
export function fmtEtaClock(iso) {
  if (!iso) return null;
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return null;
  return t.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

/** 3.2 -> "3.2 mi". Anything under a tenth of a mile is not worth printing. */
export function fmtEtaMiles(miles) {
  if (miles === null || miles === undefined) return null;
  const n = Number(miles);
  if (!Number.isFinite(n) || n < 0.1) return null;
  return `${n.toFixed(1)} mi`;
}

/**
 * The whole thing on one line: "about 25 min (14:35) · 3.2 mi".
 * Returns null when there is nothing worth showing, so a caller can skip the
 * element entirely rather than render an empty one.
 */
export function fmtEta({ etaMinutes, etaAt, etaMiles }) {
  const mins = fmtEtaMinutes(etaMinutes);
  if (!mins) return null;
  const clock = fmtEtaClock(etaAt);
  const miles = fmtEtaMiles(etaMiles);
  return `about ${mins}${clock ? ` (${clock})` : ""}${miles ? ` · ${miles}` : ""}`;
}

/** Why the estimate is worth what it is worth, for a tooltip. */
export function etaBasisText(basis) {
  if (basis === "driver") {
    return "Measured from the driver's last position update, then along the"
      + " remaining stops in route order, allowing two minutes at each door.";
  }
  if (basis === "last_drop") {
    return "The driver's position is not current, so this is measured from"
      + " their last completed delivery, then along the remaining stops in"
      + " route order, allowing two minutes at each door.";
  }
  if (basis === "branch") {
    return "The van has not reported a position yet, so this is measured from"
      + " the branch, then along the remaining stops in route order, allowing"
      + " two minutes at each door.";
  }
  return "An estimate from the remaining stops in route order, allowing two"
    + " minutes at each door.";
}
